import React, {Component} from "react";
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {LocalStorage} from "../../utils/LocalStorage";
import {LoginWithGoogle} from "./LoginWithGoogle";
import {Link, Navigate} from "react-router-dom";
import * as Sentry from '@sentry/react';
import {Loading} from "../Loading";
import {FaIcon} from "../FaIcon";
import {isNetworkError} from "../../utils/apiResponse";
import { withTranslation } from "react-i18next";

class LoginAcceptingInvitation extends Component {
    constructor(props) {
        super(props);

        this.state = {
            // null: cargando | false: sin sesion (login Google) | 'authenticated': con sesion | true: aceptada
            isLoggedIn: null,
            accepting: false,
            error: null
        };

        this.handleGoogleLoginSuccessful = this.handleGoogleLoginSuccessful.bind(this);
        this.handleAcceptAuthenticated = this.handleAcceptAuthenticated.bind(this);
        this.handleUseAnotherAccount = this.handleUseAnotherAccount.bind(this);
        this.handleRetry = this.handleRetry.bind(this);
        this.acceptButtonRef = React.createRef();
    }

    componentDidMount() {
        // El backend es la fuente de verdad para validar la aceptacion (identidad + agencia).
        // Aca solo decidimos por que camino entrar: con sesion previa (aceptacion autenticada)
        // o sin sesion (login con Google).
        this.setState({
            isLoggedIn: LocalStorage.hasUserData() ? 'authenticated' : false
        });
    }

    handleGoogleLoginSuccessful(accountData) {
        const invitationData = {
            ...accountData,
            hash: this.props.hash
        };

        new APIClient(API_URL + 'invitation/accept').call('POST', invitationData)
            .then((response) => this._handleAcceptResponse(response, {recoverable: false}));
    }

    handleAcceptAuthenticated() {
        this.setState({accepting: true, error: null});

        new APIClient(API_URL + 'invitation/accept-authenticated').call('POST', {hash: this.props.hash})
            .then((response) => {
                this.setState({accepting: false});
                this._handleAcceptResponse(response, {recoverable: true});
            });
    }

    /**
     * Cada código dice qué pasó y qué hacer. Hasta el 24/09 sólo dos tenían copy propio y el resto
     * —«ya aceptada», el tope de usuarios cliente, un microcorte— caía en «No pudimos aceptar la
     * invitación», que no deja al invitado hacer nada más que escribir a soporte (Not Ordinary
     * Agency, ticket wa:not-ordinary-agency-2026-09-24).
     *
     * `recoverable` = hay sesión abierta y se ofrece «Usar otra cuenta». `retryable` = vale la pena
     * volver a intentar con la misma cuenta.
     */
    _handleAcceptResponse(response, {recoverable}) {
        const {t} = this.props;

        if (response.status === 'OK') {
            this.handleLoginSuccessful(response.data);
            return;
        }

        let message;
        let retryable = false;
        let alreadyAccepted = false;
        if (response.status === 'ERROR_EMAIL_MISMATCH') {
            message = t('login.invitation_email_mismatch', {email: this.props.email});
        } else if (response.status === 'ERROR_EXISTING_USER') {
            message = t('login.invitation_existing_agency_generic');
        } else if (response.status === 'ERROR_ALREADY_ACCEPTED') {
            // Con sesión abierta ya está adentro: pedirle que entre con Google lo mandaría a
            // loguearse de nuevo.
            message = recoverable ? t('login.invitation_already_member') : t('login.invitation_already_accepted');
            alreadyAccepted = true;
        } else if (response.status === 'CLIENT_USERS_LIMIT_REACHED') {
            message = t('login.invitation_client_users_limit');
        } else if (isNetworkError(response)) {
            message = t('login.invitation_network_error');
            retryable = true;
        } else {
            message = t('login.invitation_generic_error');
            retryable = true;
            this._reportUnexpectedError(response, recoverable);
        }

        this.setState({error: {message, recoverable, retryable, alreadyAccepted}});
    }

    /**
     * Los 5xx y los fallos de red ya los reporta el `APIClient` (`_reportServerError` y su
     * `.catch`). Lo que no llega a Sentry por ningún lado es un código tipado que esta pantalla no
     * conoce: un 422 resuelve la promesa como cualquier respuesta, y el invitado se queda con el
     * genérico sin rastro.
     */
    _reportUnexpectedError(response, recoverable) {
        if (!response || Number(response.http_status) >= 500) return;
        try {
            Sentry.captureMessage(`Aceptar invitación: código no contemplado ${response.status}`, {
                level: 'warning',
                fingerprint: ['invitation-accept-unexpected', String(response.status)],
                tags: {invitation_accept_status: String(response.status),
                       invitation_accept_path: recoverable ? 'authenticated' : 'google'},
            });
        } catch (e) {
            // Reportar nunca puede romper la pantalla del invitado.
        }
    }

    handleRetry() {
        // «Reintentar» se desmonta al volver al botón de aceptar: sin mover el foco, quien usa el
        // teclado quedaba en el <body> y tenía que tabular desde el principio (QA-VIS-invit-13).
        this.setState({error: null}, () => {
            if (this.acceptButtonRef.current) this.acceptButtonRef.current.focus();
        });
    }

    handleUseAnotherAccount() {
        // Limpiar la sesion actual y volver al login con Google para identificarse con la cuenta correcta.
        LocalStorage.removeUserData();
        this.setState({isLoggedIn: false, error: null, accepting: false});
    }

    handleLoginSuccessful(responseData) {
        const userData = responseData.logged_user_data;

        LocalStorage.saveUserData(userData.user_id, userData.token, userData.user_type, userData.agency_status, userData.language, userData.client_id || null, userData.is_dashbo_mcp, userData.free_trial_days_remaining, userData.agency_logo_url, userData.agency_display_name, userData.brand_primary_color, userData.brand_secondary_color, userData.portal_theme);
        LocalStorage.updateTrialNextStep(userData.trial_next_step);
        LocalStorage.saveUserClients(userData.clients || null);

        this.setState({isLoggedIn: true});
    }

    _homePath() {
        return {pathname: "/"};
    }

    renderError() {
        const {t} = this.props;
        const {error} = this.state;
        if (!error) {
            return null;
        }

        return (
            <div className="invitation-accept-error">
                <FaIcon iconName="circle-exclamation" className="invitation-accept-error-icon"/>
                <p className="invitation-accept-error-text">{error.message}</p>
                {error.alreadyAccepted && (
                    <Link className="btn -alt" to="/">{t('login.invitation_go_to_dashbo')}</Link>
                )}
                {/* Con sesión, el error reemplaza al botón de aceptar: sin «Reintentar» el
                    invitado no tenía cómo volver a probar. Sin sesión, el botón de Google sigue
                    abajo y ése es el reintento. */}
                {error.retryable && error.recoverable && (
                    <button className="btn -alt" onClick={this.handleRetry}>
                        {t('login.invitation_retry')}
                    </button>
                )}
                {error.recoverable && (
                    <button className="btn -alt -negative" onClick={this.handleUseAnotherAccount}>
                        {t('login.use_another_account')}
                    </button>
                )}
            </div>
        );
    }

    render() {
        const {t} = this.props;

        if (this.state.isLoggedIn === null) {
            return <Loading message={t('login.wait')}/>;
        }

        if (this.state.isLoggedIn === true) {
            return <Navigate to={this._homePath()}/>;
        }

        if (this.state.isLoggedIn === 'authenticated') {
            return (
                <div className="invitation-accept-section">
                    {this.renderError()}
                    {!this.state.error && (
                        <button
                            className="btn -alt"
                            ref={this.acceptButtonRef}
                            onClick={this.handleAcceptAuthenticated}
                            disabled={this.state.accepting}
                        >
                            {this.state.accepting ? t('login.wait') : t('login.accept_invitation')}
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div className="invitation-accept-section">
                {this.renderError()}
                <LoginWithGoogle
                    onGoogleLoginSucessful={this.handleGoogleLoginSuccessful}
                    buttonText={t('login.accept_invitation')}
                />
            </div>
        );
    }
}

export default withTranslation()(LoginAcceptingInvitation);
